import assign from 'lodash/assign';
import isArray from 'lodash/isArray';

type CharacterSequence = (string | RegExp)[];
type TokenParser<T> = (t: T) => string;
type TimeoutId = ReturnType<typeof setTimeout>;

type Config<T> = {
    // Function to extract text from tokens
    parseToken: TokenParser<T>;

    // Function to call when text is emitted from the typing service
    onEmitText: (text: string) => void;

    // Function to call when the typing state changes
    onTypingStateChange: (isTyping: boolean) => void;

    // Sequences that will prevent text from being typed if partially present in typing buffer
    blockingSequences: CharacterSequence[][];
};

export const AT_MENTIONS_SEQUENCE: CharacterSequence[] = [
    ['@', '{', '{'],
    ['}', '}'],
];

export const CLICKABLE_OPTION_SEQUENCE: CharacterSequence[] = [
    ['[', '['],
    [']', ']'],
];

/**
 * Service that can be used to create a fake "typing" effect of text. Uses timer
 * delays to mimic human typing, and can prevent sequences of tokens from being
 * 'typed' until fully completed. This can be used to, for example, prevent
 * partially completed markdown nodes from being displayed.
 */
export class TypingService<T> {
    private readonly config: Config<T> = {
        parseToken: (t) => t as string,
        onEmitText: () => {},
        onTypingStateChange: () => {},
        blockingSequences: [],
    };

    // Boolean indicating if actively typing
    isTyping: boolean = false;

    // Boolean indicating if typing has ever been started, even if not actively typing
    isTypingStarted: boolean = false;

    // Buffer to accumulate text that will be typed
    private textBuffer: string = '';

    // The number of tokens used to build the current text buffer
    private textBufferTokenCount: number = 0;

    // Buffer to accumulate tokens that will be typed
    private tokenBuffer: T[] = [];

    // Track when active typing starts
    private startTime: number = 0;

    // The id of the active timer used to mimic typing delays
    private timeoutId: TimeoutId | undefined;

    constructor (config?: Partial<Config<T>>) {
        if (config) {
            assign(this.config, config);
        }
    }

    addTokens (tokens: T | T[]) {
        const tokensArray = isArray(tokens) ? tokens : [tokens];
        this.tokenBuffer.push(...tokensArray);

        if (this.isTyping) return;

        this.isTypingStarted = true;
        this._setTypingState(true);
        this.startTime = Date.now();
        this._type();
    }

    reset () {
        if (this.timeoutId !== undefined) {
            clearTimeout(this.timeoutId);
            this.timeoutId = undefined;
        }
        this._setTypingState(false);
        this.isTypingStarted = false;
        this.tokenBuffer = [];
        this.textBuffer = '';
        this.textBufferTokenCount = 0;
    }

    private _canFlushTextBuffer (): boolean {
        for (const sequence of this.config.blockingSequences) {
            if (containsPartialSequence(this.textBuffer, sequence)) {
                return false;
            }
        }

        return true;
    }

    private _flushTextBuffer () {
        this.config.onEmitText(this.textBuffer);
        this.textBuffer = '';
        this.textBufferTokenCount = 0;
    }

    private _setTypingState (isTyping: boolean) {
        if (this.isTyping === isTyping) return;

        this.isTyping = isTyping;
        this.config.onTypingStateChange(isTyping);
    }

    private _type () {
        const token = this.tokenBuffer.shift();

        if (!token) {
            this._setTypingState(false);

            return;
        }

        const elapsedTime = Date.now() - this.startTime;
        const text = this.config.parseToken(token);
        this.textBuffer += text;
        this.textBufferTokenCount += 1;

        if (this._canFlushTextBuffer()) {
            this._flushTextBuffer();
        }

        const remaining = this.tokenBuffer.length;
        const minDelay = elapsedTime > 5000 ? 1 : 20;
        const maxDelay = elapsedTime > 5000 ? 3 : 30;
        const delay = this.textBufferTokenCount > 1 ? 0 : Math.max(minDelay, maxDelay - remaining * 2);

        this.timeoutId = setTimeout(() => this._type(), delay);
    }
}

/**
 * Check if the provided text contains some but not all of the provided sequences,
 * or if we're in the middle of matching a sequence when the text ends.
 * Each sequence is an array of patterns (strings or RegExp) that must be matched consecutively.
 * Sequences must be found in order and cannot overlap, but can be adjacent.
 *
 * @param text - The text to search in
 * @param sequences - Array of sequences, where each sequence is an array of patterns to match in order
 * @returns true if some (but not all) sequences are found or if partially matching a sequence, false otherwise
 */
export function containsPartialSequence (text: string, sequences: CharacterSequence[]) {
    if (!sequences.length) return false;

    let textIndex = 0;
    let foundCount = 0;

    for (const sequence of sequences) {
        const { found, endIndex, partialMatch } = findSequenceInText(text, sequence, textIndex);
        if (found) {
            foundCount++;
            textIndex = endIndex;
        } else if (partialMatch) {
            // We're in the middle of matching a sequence when text ended
            return true;
        } else {
            // Stop searching once we hit a sequence that can't be found
            break;
        }
    }

    // Return true only if we found some but not all sequences
    return foundCount > 0 && foundCount < sequences.length;
}

/**
 * Find a sequence of patterns in text starting from a given index.
 *
 * @param text - The text to search in
 * @param sequence - Array of patterns to match in order
 * @param startIndex - Index to start searching from
 * @returns Object with found (boolean), partialMatch (boolean), and endIndex (number)
 */
export function findSequenceInText (
    text: string,
    sequence: CharacterSequence,
    startIndex: number,
): { found: boolean; partialMatch: boolean; endIndex: number } {
    let textIndex = startIndex;
    let sequenceIndex = 0;

    if (sequence.length === 0) {
        return {
            found: false,
            partialMatch: false,
            endIndex: -1,
        };
    }

    while (textIndex < text.length) {
        const char = text[textIndex];
        const currentPattern = sequence[sequenceIndex];

        const isMatch = typeof currentPattern === 'string' ? char === currentPattern : currentPattern.test(char);

        if (isMatch) {
            sequenceIndex++;
            textIndex++;

            if (sequenceIndex >= sequence.length) {
                return { found: true, partialMatch: false, endIndex: textIndex };
            }
        } else {
            // Check if current character could start the sequence from the beginning
            const firstPattern = sequence[0];
            const isFirstPatternMatch =
                typeof firstPattern === 'string' ? char === firstPattern : firstPattern.test(char);

            if (isFirstPatternMatch) {
                sequenceIndex = 1; // We matched the first pattern
                textIndex++;
            } else {
                sequenceIndex = 0;
                textIndex++;
            }
        }
    }

    // If we reached the end of text while in the middle of a sequence match
    if (sequenceIndex > 0) {
        return { found: false, partialMatch: true, endIndex: textIndex };
    }

    return { found: false, partialMatch: false, endIndex: -1 };
}
